import React from "react";
import { Input } from "../ui/input2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import { SearchableSelect } from "./SearchableSelect";

interface SelectOption {
  value: string;
  label: string;
}

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  type?: "text" | "email" | "tel" | "url" | "select" | "textarea" | "number" | "searchable-select";
  placeholder?: string;
  options?: SelectOption[];
  className?: string;
  error?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  options = [],
  className = "",
  error,
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange(name, e.target.value);
  };

  const handleSelectChange = (selectedValue: string) => {
    onChange(name, selectedValue);
  };

  return (
    <div className={className}>
      <div className="flex justify-between">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label.includes('*') ? (
            <>
              {label.replace('*', '').trim()}{' '}
              <span className="text-red-500">*</span>
            </>
          ) : (
            label
          )}
        </label>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>

      {type === "select" ? (
        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger className={`rounded-lg bg-gray-100 text-sm text-gray-900 shadow-sm transition-all duration-150 ease-in-out border-transparent focus:border-gray-300 focus:ring-2 focus:ring-[#885ABB] hover:bg-gray-50 focus:bg-white ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}>
            <SelectValue
              placeholder={placeholder || `Select ${label.toLowerCase()}`}
            />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : type === "searchable-select" ? (
        <SearchableSelect
          options={options}
          value={value}
          onChange={handleSelectChange}
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          className={error ? 'border-red-500' : ''}
          error={error}
        />
      ) : type === "textarea" ? (
        <textarea
          name={name}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder || `Enter ${label.toLowerCase()}`}
          rows={3}
          className={`w-full px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-150 ease-in-out outline-none border border-transparent focus:border-gray-300 focus:ring-2 focus:ring-[#885ABB] hover:bg-gray-50 focus:bg-white resize-none ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
        />
      ) : (
        <Input
          type={type}
          name={name}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder || `Enter ${label.toLowerCase()}`}
          className={error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
        />
      )}
    </div>
  );
};

export default FormField;
